"use client";

import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { AlertCircle, CheckCircle, Zap, PlayCircle, Settings, ShieldCheck, ArrowRight } from "lucide-react";
import { authService } from "../../apis/authService";
import { useAuth } from "../../context/AuthContext";

export function VerifyAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshMe, logout } = useAuth();

  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isVerified, setIsVerified] = useState(false);
  const [isAlreadyVerified, setIsAlreadyVerified] = useState(false);
  const [isOnboardingComplete, setIsOnboardingComplete] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [userId, setUserId] = useState(null);
  const [resendStatus, setResendStatus] = useState(null); // 'sending' | 'success' | 'error'
  const [countdown, setCountdown] = useState(0);

  const token = searchParams.get("token");
  const id = searchParams.get("id");

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  useEffect(() => {
    const verify = async () => {
      if (!token) {
        setMessage({ type: "error", text: "Invalid verification link" });
        setIsLoading(false);
        return;
      }

      try {
        const result = await authService.verifyEmail(token, id);
        if (result.success) {
          if (result.alreadyVerified) {
            setIsAlreadyVerified(true);
            setIsOnboardingComplete(result.onboardingComplete);
            if (!result.onboardingComplete) {
              await refreshMe();
            }
          } else {
            setIsVerified(true);
            // Try to refresh user auth state so they are logged in directly
            await refreshMe();
          }
        } else {
          setMessage({ type: "error", text: result.message || "Verification failed" });
          if (result.expired) {
            setIsExpired(true);
            setUserId(result.userId);
          }
        }
      } catch (err) {
        setMessage({ type: "error", text: "Verification failed. Please try again." });
      } finally {
        setIsLoading(false);
      }
    };

    verify();
  }, [token, id, refreshMe]);

  const handleResend = async () => {
    if (!userId || countdown > 0) return;
    setResendStatus("sending");
    try {
      const res = await authService.resendOtp(userId, "signup");
      if (res.success) {
        setResendStatus("success");
        setCountdown(60); // 60 seconds countdown
      } else {
        setResendStatus("error");
      }
    } catch (err) {
      setResendStatus("error");
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 space-y-5">
        <div className="w-14 h-14 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="text-base sm:text-lg font-medium text-gray-600">Verifying your account...</p>
      </div>
    );
  }

  if (isVerified) {
    return (
      <div className="space-y-6 py-4 text-center">
        {/* Success Icon with modern pulsing ring */}
        <div className="relative flex items-center justify-center w-24 h-24 mx-auto mb-3">
          <div className="absolute inset-0 bg-teal-100 rounded-full animate-ping opacity-25"></div>
          <div className="relative w-20 h-20 bg-teal-50 text-teal-600 rounded-full flex items-center justify-center border border-teal-100 shadow-inner">
            <CheckCircle size={44} className="stroke-[2.5]" />
          </div>
        </div>

        {/* Text Details */}
        <div className="space-y-3">
          <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Account Verified!</h3>
          <p className="text-base sm:text-lg text-gray-600 max-w-md mx-auto leading-relaxed">
            Welcome to <span className="font-semibold text-teal-600">VirtualScribe</span>. Your account is active and your 7 days free trial has begun.
          </p>
        </div>

        {/* Feature Highlights */}
        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-100 text-left max-w-md mx-auto space-y-3">
          <div className="flex items-start gap-3.5">
            <Zap className="w-6 h-6 text-teal-600 mt-0.5 shrink-0" />
            <div>
              <p className="text-base font-semibold text-slate-800">Ambient AI Scribing</p>
              <p className="text-sm sm:text-base text-slate-600 mt-0.5 leading-relaxed">Record patient visits naturally and get SOAP notes in seconds.</p>
            </div>
          </div>
        </div>

        {/* CTA Button */}
        <div className="pt-2 max-w-md mx-auto">
          <Link
            to="/onboarding"
            className="group w-full h-[52px] bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white rounded-xl font-semibold text-base sm:text-lg flex items-center justify-center gap-2 shadow-md transition duration-200"
          >
            Start Your Onboarding
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    );
  }
  if (isAlreadyVerified) {
    return (
      <div className="space-y-6 py-4 text-center">
        {/* Success Icon with modern pulsing ring */}
        <div className="relative flex items-center justify-center w-24 h-24 mx-auto mb-3">
          <div className="relative w-20 h-20 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center border border-blue-100 shadow-inner">
            <CheckCircle size={44} className="stroke-[2.5]" />
          </div>
        </div>

        {/* Text Details */}
        <div className="space-y-3">
          <h3 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Already Verified</h3>
          <p className="text-base sm:text-lg text-gray-600 max-w-md mx-auto leading-relaxed">
            {isOnboardingComplete
              ? "Your email is already verified. You are an existing customer. Please sign in to access your account."
              : "You are already verified. Complete the onboarding."}
          </p>
        </div>

        {/* CTA Button */}
        <div className="pt-2 max-w-md mx-auto">
          <Link
            to={isOnboardingComplete ? "/signin" : "/onboarding"}
            className="group w-full h-[52px] bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white rounded-xl font-semibold text-base sm:text-lg flex items-center justify-center gap-2 shadow-md transition duration-200"
          >
            {isOnboardingComplete ? "Sign In to Your Account" : "Complete Your Onboarding"}
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5 py-4">
      {message && (
        <div className={`p-4 rounded-xl flex items-start gap-3.5 ${message.type === "success" ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`}>
          {message.type === "success" ? (
            <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
          )}
          <div>
            <span className="text-base sm:text-lg font-semibold block">{message.type === "success" ? "Success" : "Verification Error"}</span>
            <span className="text-sm sm:text-base font-medium block mt-1 leading-normal">{message.text}</span>
          </div>
        </div>
      )}
      {isExpired && (
        <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 space-y-4 mt-4">
          <p className="text-base text-gray-600 text-center font-medium">
            Would you like us to send you a new activation link?
          </p>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendStatus === "sending" || countdown > 0}
            className={`w-full h-[52px] rounded-xl font-semibold text-base sm:text-lg flex items-center justify-center gap-2 shadow-sm transition duration-200 ${countdown > 0
              ? "bg-slate-100 text-slate-400 border border-slate-200 cursor-not-allowed"
              : "bg-gradient-to-r from-blue-600 to-teal-600 hover:shadow-lg text-white cursor-pointer"
              }`}
          >
            {resendStatus === "sending" && "Sending new link..."}
            {countdown > 0 && `Resend Link again in ${countdown}s`}
            {resendStatus === "success" && countdown === 0 && "Welcome email resent! Resend again?"}
            {resendStatus === "error" && countdown === 0 && "Failed to resend. Try again"}
            {!resendStatus && countdown === 0 && "Resend Verification Email"}
          </button>
        </div>
      )}
      <div className="pt-4 text-center flex flex-col items-center justify-center gap-3">
        <button
          type="button"
          onClick={async () => {
            await logout();
            navigate("/signin");
          }}
          className="text-base text-blue-600 hover:text-blue-700 font-semibold transition bg-transparent border-none cursor-pointer"
        >
          Return to Sign In
        </button>
        <span className="text-gray-400 text-base">or</span>
        <button
          type="button"
          onClick={async () => {
            await logout();
            navigate("/signup");
          }}
          className="text-base text-gray-500 hover:text-gray-600 font-medium transition bg-transparent border-none cursor-pointer"
        >
          Return to Sign Up
        </button>
      </div>
    </div>
  );
}